import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { compose, filter, includes, map } from 'ramda';
import { useDispatch } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import codelistProvider from 'components/decorators/codelistProvider';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { resetFormCriteria, searchSemaphoreList } from 'features/views/semaphore/actions';
import { productDomainsSelectAllOption, unspecifiedFeedbackOption } from 'features/views/semaphore/constants';
import { FIELDS, SEMAPHORE_FORM_NAME } from 'features/views/semaphore/form';
import { useFilteredProductDomainsOptionsByField } from 'features/views/semaphore/hooks';

import ImpendingStateAutoComplete from './ImpendingStateAutoComplete';
import SemaphoreAutoComplete from './SemaphoreAutoComplete';

const SemaphoreForm = ({ productDomains, semaphoreFeedback }) => {
	const dispatch = useDispatch();
	const next = useControlNext(SEMAPHORE_FORM_NAME);

	const productDomainsOptions = useFilteredProductDomainsOptionsByField(
		FIELDS.notInsuredProductDomain,
		productDomains,
	);

	const notInsuredProductDomainsOptions = useFilteredProductDomainsOptionsByField(
		FIELDS.productDomain,
		productDomains,
	);

	const handlePossibleSelectAll = useCallback(
		(fieldName) => (e, selectedOptions) => {
			if (includes(productDomainsSelectAllOption.id, selectedOptions)) {
				let options;
				if (fieldName === FIELDS.productDomain) {
					options = productDomainsOptions;
				} else {
					options = notInsuredProductDomainsOptions;
				}

				const optionIds = compose(
					map((option) => option?.id),
					filter((option) => option?.id !== productDomainsSelectAllOption.id),
				)(options);

				next(setValue({ fieldName, value: optionIds }));
			}
		},
		[next, notInsuredProductDomainsOptions, productDomainsOptions],
	);

	const handleResetFormCriteria = useCallback(() => {
		dispatch(resetFormCriteria());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(searchSemaphoreList());
	}, [dispatch]);

	const semaphoreFeedbackWithUnspecifiedOptionAdded = [...semaphoreFeedback, unspecifiedFeedbackOption];

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.surname}
							label={getLabelMessage('semaphore:surname')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.SURNAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.firstName}
							label={getLabelMessage('semaphore:firstName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:birthNumber')}>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.personIn}
								label={getLabelMessage('semaphore:personIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.BIRTH_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Tooltip title={getLabelMessage('tooltips:taxNumber')}>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.businessIn}
								label={getLabelMessage('semaphore:businessIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.TAX_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={2}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.productDomain}
							label={getLabelMessage('semaphore:productDomain')}
							options={productDomainsOptions}
							labelPropName="description"
							multiple
							onChange={handlePossibleSelectAll(FIELDS.productDomain)}
						/>
					</Grid>
					<Grid size={2}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.notInsuredProductDomain}
							label={getLabelMessage('semaphore:notInsuredProductDomain')}
							options={notInsuredProductDomainsOptions}
							labelPropName="description"
							multiple
							onChange={handlePossibleSelectAll(FIELDS.notInsuredProductDomain)}
						/>
					</Grid>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.birthYearFrom}
							label={getLabelMessage('semaphore:birthYearFrom')}
							placeholder={getLabelMessage('dateFormats:yyyy')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.YEAR_DATE,
								},
							}}
						/>
					</Grid>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.birthYearTo}
							label={getLabelMessage('semaphore:birthYearTo')}
							placeholder={getLabelMessage('dateFormats:yyyy')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.YEAR_DATE,
								},
							}}
						/>
					</Grid>
					<Grid size={4}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.feedback}
							label={getLabelMessage('semaphore:feedback')}
							options={semaphoreFeedbackWithUnspecifiedOptionAdded}
							labelPropName="description"
						/>
					</Grid>
					<Grid size={4}>
						<TextField
							size="small"
							fieldName={FIELDS.municipality}
							label={getLabelMessage('semaphore:municipality')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.MUNICIPALITY,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:postalCode')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.zipCode}
								label={getLabelMessage('semaphore:zipCode')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.ZIP_CODE,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={3}>
						<SemaphoreAutoComplete fieldName={FIELDS.semaphore} />
					</Grid>
					<Grid size={3}>
						<ImpendingStateAutoComplete fieldName={FIELDS.impendingState} />
					</Grid>
					<Grid size={8} />
					<Grid size={2}>
						<Button variant="outlined" onClick={handleResetFormCriteria} fullWidth>
							{getLabelMessage('common:resetButton')}
						</Button>
					</Grid>
					<Grid size={2}>
						<SubmitButton
							variant="contained"
							onClick={handleSubmit}
							fullWidth
							text={getLabelMessage('common:searchButton')}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

SemaphoreForm.propTypes = {
	productDomains: PropTypes.array,
	semaphoreFeedback: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.semaphoreFeedback, 'semaphoreFeedback', true),
	codelistProvider(codelists.productDomains, 'productDomains', true),
)(SemaphoreForm);
